/**
 * API service for Community features
 */
import axios from 'axios';
import { apiUrl } from './config';
import { getAuthToken } from '@/utils/auth';

const BASE_URL = 'community';

/**
 * Get authentication headers
 */
const getHeaders = () => {
  const token = getAuthToken('student') || getAuthToken('teacher') || getAuthToken('admin') || getAuthToken('guardian');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
};

/**
 * Get headers for file uploads
 */
const getUploadHeaders = () => {
  const token = getAuthToken('student') || getAuthToken('teacher') || getAuthToken('admin') || getAuthToken('guardian');
  return {
    'Authorization': `Bearer ${token}`,
    // Don't set Content-Type for multipart/form-data - browser will set it with boundary
  };
};

/**
 * Posts API
 */
export const postsAPI = {
  /**
   * Get announcements for Announcements tab
   */
  getAnnouncements: async (page = 1, pageSize = 20) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/announcements/`),
      {
        params: { page, page_size: pageSize },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get personalized feed for My Feed tab
   */
  getMyFeed: async (page = 1, pageSize = 20) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/my-feed/`),
      {
        params: { page, page_size: pageSize },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get all posts for Posts tab
   */
  getAllPosts: async (page = 1, pageSize = 20) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/posts-list/`),
      {
        params: { page, page_size: pageSize },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get saved posts for Saved Posts tab
   */
  getSavedPosts: async (page = 1, pageSize = 20) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/saved-posts/`),
      {
        params: { page, page_size: pageSize },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get a single post by ID
   */
  getPost: async (postId) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/posts/${postId}/`),
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Create a new post
   */
  createPost: async (postData) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/posts/`),
      postData,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Update a post
   */
  updatePost: async (postId, postData) => {
    const response = await axios.patch(
      apiUrl(`${BASE_URL}/posts/${postId}/`),
      postData,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Delete a post
   */
  deletePost: async (postId) => {
    const response = await axios.delete(
      apiUrl(`${BASE_URL}/posts/${postId}/`),
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Like/unlike a post
   */
  likePost: async (postId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/posts/${postId}/like/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Save/unsave a post
   */
  savePost: async (postId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/posts/${postId}/save/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Track view on a post
   */
  viewPost: async (postId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/posts/${postId}/view/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Pin/unpin a post (admin only)
   */
  pinPost: async (postId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/posts/${postId}/pin/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },
};

/**
 * Comments API
 */
export const commentsAPI = {
  /**
   * Get comments for a post
   */
  getComments: async (postId) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/comments/`),
      {
        params: { post_id: postId },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Create a comment
   */
  createComment: async (postId, content, parentCommentId = null) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/comments/`),
      {
        post: postId,
        content,
        parent_comment: parentCommentId,
      },
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Reply to a comment
   */
  replyToComment: async (commentId, content) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/comments/${commentId}/reply/`),
      { content },
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Update a comment
   */
  updateComment: async (commentId, content) => {
    const response = await axios.patch(
      apiUrl(`${BASE_URL}/comments/${commentId}/`),
      { content },
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Delete a comment
   */
  deleteComment: async (commentId) => {
    const response = await axios.delete(
      apiUrl(`${BASE_URL}/comments/${commentId}/`),
      { headers: getHeaders() }
    );
    return response.data;
  },
};

/**
 * Communities API
 */
export const communitiesAPI = {
  /**
   * Get all communities
   */
  getCommunities: async () => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/communities/`),
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Get a single community
   */
  getCommunity: async (communityId) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/communities/${communityId}/`),
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Create a community (admin only)
   */
  createCommunity: async (communityData) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/communities/`),
      communityData,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Join a community
   */
  joinCommunity: async (communityId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/communities/${communityId}/join/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Leave a community
   */
  leaveCommunity: async (communityId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/communities/${communityId}/leave/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Get community members
   */
  getCommunityMembers: async (communityId) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/communities/${communityId}/members/`),
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Add a member to a community (admin/teacher only)
   * Can accept either userId or username
   */
  addMember: async (communityId, userId = null, username = null) => {
    const data = {};
    if (userId) {
      data.user_id = userId;
    } else if (username) {
      data.username = username;
    } else {
      throw new Error('Either userId or username is required');
    }
    
    const response = await axios.post(
      apiUrl(`${BASE_URL}/communities/${communityId}/add_member/`),
      data,
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Remove a member from a community (admin/teacher only, or user themselves)
   */
  removeMember: async (communityId, userId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/communities/${communityId}/remove_member/`),
      { user_id: userId },
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Get autocomplete suggestions for post creation (@grade, @division, @community, @user)
   */
  getAutocomplete: async (query, type = 'user') => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/autocomplete/`),
      {
        params: { q: query, type },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get hashtag autocomplete suggestions
   */
  getHashtagAutocomplete: async (query) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/hashtag-autocomplete/`),
      {
        params: { q: query },
        headers: getHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Search for users to add to communities (admin/teacher only)
   */
  searchUsers: async (query) => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/search-users/`),
      {
        params: { q: query },
        headers: getHeaders(),
      }
    );
    return response.data;
  },
};

/**
 * Notifications API
 */
export const notificationsAPI = {
  /**
   * Get notifications
   */
  getNotifications: async () => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/notifications/`),
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Mark notification as read
   */
  markAsRead: async (notificationId) => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/notifications/${notificationId}/mark_read/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async () => {
    const response = await axios.post(
      apiUrl(`${BASE_URL}/notifications/mark_all_read/`),
      {},
      { headers: getHeaders() }
    );
    return response.data;
  },

  /**
   * Get unread notification count
   */
  getUnreadCount: async () => {
    const response = await axios.get(
      apiUrl(`${BASE_URL}/notifications/unread_count/`),
      { headers: getHeaders() }
    );
    return response.data;
  },
};

/**
 * File Upload API
 */
export const uploadAPI = {
  /**
   * Upload an image
   */
  uploadImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      apiUrl(`${BASE_URL}/upload/image/`),
      formData,
      {
        headers: getUploadHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Upload a file
   */
  uploadFile: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await axios.post(
      apiUrl(`${BASE_URL}/upload/file/`),
      formData,
      {
        headers: getUploadHeaders(),
      }
    );
    return response.data;
  },
};
